import React from 'react';
import {Box, Typography} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * AnalystResponseContent - Displays markdown response from analyst perspectives
 * Similar to GeneratedContentContainer but simplified without borders/padding
 */
const AnalystResponseContent = ({markdown, error}) => {
    if (error) {
        return (
            <Box sx={{color: 'error.main'}}>
                <Typography variant="body2">
                    Error: {error}
                </Typography>
            </Box>
        );
    }

    if (!markdown || markdown.trim().length === 0) {
        return null;
    }

    return (
        <Box
            sx={{
                '& h1': {
                    fontSize: '1.75rem',
                    fontWeight: 600,
                    mt: 2,
                    mb: 1,
                },
                '& h2': {
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    mt: 2,
                    mb: 1,
                },
                '& h3': {
                    fontSize: '1.25rem',
                    fontWeight: 600,
                    mt: 1.5,
                    mb: 0.75,
                },
                '& h4': {
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    mt: 1.5,
                    mb: 0.75,
                },
                '& h5': {
                    fontSize: '1rem',
                    fontWeight: 600,
                    mt: 1,
                    mb: 0.5,
                },
                '& h6': {
                    fontSize: '0.9rem',
                    fontWeight: 600,
                    mt: 1,
                    mb: 0.5,
                },
                '& p': {
                    mb: 1,
                    lineHeight: 1.6,
                },
                '& ul, & ol': {
                    pl: 3,
                    mb: 1,
                },
                '& li': {
                    mb: 0.5,
                },
                '& blockquote': {
                    borderLeft: '4px solid',
                    borderColor: 'divider',
                    pl: 2,
                    py: 0.5,
                    my: 1,
                    fontStyle: 'italic',
                    color: 'text.secondary',
                },
                '& code': {
                    backgroundColor: 'action.hover',
                    px: 0.5,
                    py: 0.25,
                    borderRadius: 0.5,
                    fontFamily: 'monospace',
                    fontSize: '0.9em',
                },
                '& pre': {
                    backgroundColor: 'action.hover',
                    p: 1.5,
                    borderRadius: 1,
                    overflow: 'auto',
                    mb: 1,
                    '& code': {
                        backgroundColor: 'transparent',
                        p: 0,
                    },
                },
                '& table': {
                    width: '100%',
                    borderCollapse: 'collapse',
                    mb: 1,
                },
                '& th, & td': {
                    border: '1px solid',
                    borderColor: 'divider',
                    px: 1,
                    py: 0.5,
                    textAlign: 'left',
                },
                '& th': {
                    backgroundColor: 'action.hover',
                    fontWeight: 600,
                },
                '& hr': {
                    my: 2,
                    borderColor: 'divider',
                },
                '& a': {
                    color: 'primary.main',
                    textDecoration: 'none',
                    '&:hover': {
                        textDecoration: 'underline',
                    },
                },
            }}
        >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {markdown}
            </ReactMarkdown>
        </Box>
    );
};

export default AnalystResponseContent;
